import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { adminChangePassword } from '../../services/api';
import toast from 'react-hot-toast';
import './Admin.css';

/* ── Field MUST be outside the component so React doesn't recreate it on every keystroke ── */
const Field = ({ name, label, showKey, placeholder, form, show, errors, onSetForm, onSetErrors, onToggleShow }) => (
  <div className="form-group">
    <label>{label}</label>
    <div className="password-wrap">
      <input
        type={show[showKey] ? 'text' : 'password'}
        value={form[name]}
        onChange={e => { onSetForm(name, e.target.value); onSetErrors(name); }}
        placeholder={placeholder}
        className={errors[name] ? 'input-error' : ''}
        autoComplete="new-password"
      />
      <button type="button" className="pwd-eye" onClick={() => onToggleShow(showKey)}>
        {show[showKey] ? <EyeOff size={16}/> : <Eye size={16}/>}
      </button>
    </div>
    {errors[name] && <span className="field-error"><AlertCircle size={11}/> {errors[name]}</span>}
  </div>
);

const AdminChangePassword = () => {
  const navigate = useNavigate();
  const [form,    setForm]    = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [show,    setShow]    = useState({ curr: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});

  const handleSetForm   = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSetErrors = (k)    => setErrors(p => ({ ...p, [k]: '' }));
  const handleToggle    = (k)    => setShow(p => ({ ...p, [k]: !p[k] }));

  const validate = () => {
    const e = {};
    if (!form.currentPassword) e.currentPassword = 'Enter your current password';
    if (!form.newPassword || form.newPassword.length < 8) e.newPassword = 'Minimum 8 characters';
    else if (form.newPassword === form.currentPassword)   e.newPassword = 'New password must differ from current';
    if (form.newPassword !== form.confirmPassword)        e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await adminChangePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      toast.success('Password changed successfully!');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to change password';
      toast.error(msg);
      if (msg.toLowerCase().includes('current')) setErrors({ currentPassword: msg });
    } finally { setLoading(false); }
  };

  const shared = { form, show, errors, onSetForm: handleSetForm, onSetErrors: handleSetErrors, onToggleShow: handleToggle };

  return (
    <AdminLayout>
      <div className="admin-page">
        <div className="admin-page__header">
          <div>
            <h1>Change Password</h1>
            <p>Update your admin account password</p>
          </div>
        </div>

        <div className="admin-card" style={{maxWidth:'480px'}}>
          <div className="admin-card__header">
            <h3><Lock size={16} style={{marginRight:'8px',verticalAlign:'middle'}}/>Security</h3>
          </div>

          <div style={{background:'rgba(234,46,43,0.05)',border:'1px solid rgba(234,46,43,0.15)',borderRadius:'6px',padding:'12px 16px',marginBottom:'20px',fontSize:'13px',color:'var(--text-2)'}}>
            <CheckCircle2 size={13} style={{color:'var(--success)',marginRight:'6px',verticalAlign:'middle'}}/>
            Password must be at least 8 characters. Use a mix of letters, numbers, and symbols.
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <Field {...shared} name="currentPassword" label="Current Password"     showKey="curr"    placeholder="Your current password"/>
            <Field {...shared} name="newPassword"     label="New Password"         showKey="new"     placeholder="At least 8 characters"/>
            <Field {...shared} name="confirmPassword" label="Confirm New Password" showKey="confirm" placeholder="Re-enter new password"/>

            <div style={{display:'flex',gap:'12px',marginTop:'8px'}}>
              <button type="submit" className="btn-primary" disabled={loading} style={{flex:1,justifyContent:'center'}}>
                {loading ? 'Saving...' : 'Update Password'}
              </button>
              <button type="button" className="admin-btn-secondary" onClick={() => navigate('/admin/dashboard')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminChangePassword;
